
import { defineComponent } from 'vue';
import { mapState } from 'pinia';
import { useStore } from '@/plugins/store/index';
import GenericTable from '@/components/tables/GenericTable.vue';
import { roundIfNecessary } from '@/util/helpers/numbers';
import { AllFilterSelectors } from '@/types';
import CourseThemesModal from '@/views/Courses/CourseThemesModal.vue';
import CustomSelect from '@/components/ui/CustomSelect.vue';

export default defineComponent({
    components: { GenericTable, CourseThemesModal, CustomSelect },
    data() {
        function cellRendererSelector(params: any) {
            if (params.node.rowPinned) {
                return {
                    component: 'CustomPinnedRowCellRenderer',
                };
            }
            return {
                component: 'GenericCellRenderer',
            };
        }
        return {
            selectedTrivialLimit: { value: 0, text: '0 startet' },
            trivialLimits: [
                { value: 0, text: '0 startet' },
                { value: 3, text: '3 startet' },
                { value: 5, text: '5 startet' },
                { value: 10, text: '10 startet' },
                { value: 20, text: '20 startet' },
                { value: 50, text: '50 startet' },
            ],
            headers: [
                {
                    headerName: 'Kommune',
                    field: 'title',
                    sortable: true,
                    cellRendererSelector,
                },
                {
                    headerName: 'Antal startet',
                    field: 'started',
                    sortable: true,
                    cellRendererSelector,
                },
                {
                    headerName: 'Antal afsluttet',
                    field: 'ended',
                    sortable: true,
                    cellRendererSelector,
                },
                {
                    headerName: 'Procentuelt afsluttet',
                    field: 'percentageEnded',
                    sortable: true,
                    cellRendererSelector,
                },
            ],
        };
    },
    computed: {
        ...mapState(useStore, ['regions', 'selectedRegions', 'courseStatistics']),
        pinnedBottomRowData(): any {
            const courseDistribution = this.items.reduce(
                (accum, current) => {
                    accum.started += current.started;
                    accum.ended += current.ended;
                    return accum;
                },
                { started: 0, ended: 0 }
            );
            return [
                {
                    id: AllFilterSelectors.AllCourses,
                    title: 'Total',
                    started: courseDistribution.started,
                    ended: courseDistribution.ended,
                    percentageEnded: roundIfNecessary((courseDistribution.ended / courseDistribution.started) * 100, 1) || 0,
                },
            ];
        },
        items(): any[] {
            if (!this.regions) {
                return [];
            }

            const startedWithUnknownRegion = this.courseStatistics.filter((currentCourseStatistic) => !currentCourseStatistic.region).length;
            const endedWithUnknownRegion = this.courseStatistics.filter((currentCourseStatistic) => currentCourseStatistic.ended && !currentCourseStatistic.region).length;

            return [
                ...this.regions
                    .filter((currentRegion) => {
                        return (
                            currentRegion.id !== AllFilterSelectors.AllRegions &&
                            currentRegion.excludeFromSorting !== true &&
                            (this.selectedRegions.length === 0 || Boolean(this.selectedRegions.find((currentSelectedRegion) => currentSelectedRegion.id === currentRegion.id)))
                        );
                    })
                    .map((currentRegion) => {
                        const startedForCurrentRegion = this.courseStatistics.filter((currentCourseStatistic) => currentCourseStatistic.region === currentRegion.id);
                        const endedForCurrentRegion = startedForCurrentRegion.filter((currentCourseStatistic) => currentCourseStatistic.ended);
                        return {
                            id: currentRegion.id,
                            title: currentRegion.title,
                            started: startedForCurrentRegion.length,
                            ended: endedForCurrentRegion.length,
                            percentageEnded: roundIfNecessary((endedForCurrentRegion.length / startedForCurrentRegion.length) * 100, 1) || 0,
                        };
                    })
                    .concat([
                        {
                            id: 'unknown',
                            title: 'Ikke oplyst',
                            started: startedWithUnknownRegion,
                            ended: endedWithUnknownRegion,
                            percentageEnded: roundIfNecessary((endedWithUnknownRegion / startedWithUnknownRegion) * 100, 1) || 0,
                        },
                    ])
                    .filter((currentRegion) => {
                        return currentRegion.started > this.selectedTrivialLimit.value;
                    }),
            ];
        },
    },
});
